import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';

export interface PcMultiSelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-pc-multi-select-filter',
  templateUrl: './pc-multi-select-filter.component.html',
  styleUrls: ['./pc-multi-select-filter.component.scss'],
})
export class PcMultiSelectFilterComponent implements OnChanges, OnDestroy {
  private static openInstance: PcMultiSelectFilterComponent | null = null;

  @Input() lightMode = false;

  @HostBinding('class.pc-ms-filter-host--light')
  get hostLight(): boolean {
    return this.lightMode;
  }

  @Input() label = '';
  @Input() placeholder = 'Select...';
  @Input() allLabel = 'All';
  @Input() searchPlaceholder = 'Search...';
  @Input() emptyMessage = 'No options found';
  @Input() options: string[] = [];
  /** When set, `value` is stored in selection and `label` is shown in the UI. */
  @Input() optionItems: PcMultiSelectOption[] | null = null;
  @Input() selected: string[] = [];
  @Output() selectedChange = new EventEmitter<string[]>();
  @Output() dropdownClosed = new EventEmitter<void>();

  @ViewChild('trigger') triggerRef?: ElementRef<HTMLElement>;
  @ViewChild('panel') panelRef?: ElementRef<HTMLElement>;

  dropdownOpen = false;
  searchText = '';
  /** True only after user clicks "Select All" — drives trigger "All" label. */
  private showAllLabel = false;

  private outsideClickHandler: ((event: MouseEvent) => void) | null = null;
  private outsideClickBindTimer: ReturnType<typeof setTimeout> | null = null;
  private panelPortaled = false;
  private readonly onViewportChange = () => {
    if (this.dropdownOpen) {
      this.updatePanelPosition();
    }
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private hostRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['selected'] && !changes['options'] && !changes['optionItems']) {
      return;
    }
    if (!this.selected?.length) {
      this.showAllLabel = false;
      return;
    }
    if (this.showAllLabel && !this.isFullySelected()) {
      this.showAllLabel = false;
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (
      this.dropdownOpen &&
      PcMultiSelectFilterComponent.openInstance === this
    ) {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown();
    }
  }

  ngOnDestroy(): void {
    if (PcMultiSelectFilterComponent.openInstance === this) {
      PcMultiSelectFilterComponent.openInstance = null;
    }
    this.detachPanelFromHost();
    this.closeDropdown(false);
  }

  get resolvedOptions(): PcMultiSelectOption[] {
    if (this.optionItems?.length) {
      return this.optionItems;
    }
    return (this.options || []).map((opt) => ({ value: opt, label: opt }));
  }

  get filteredOptions(): PcMultiSelectOption[] {
    const q = this.searchText.toLowerCase().trim();
    if (!q) {
      return this.resolvedOptions;
    }
    return this.resolvedOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(q) ||
        opt.value.toLowerCase().includes(q),
    );
  }

  get displayLabel(): string {
    // Empty → placeholder ("Select Client", "Select Port", …).
    if (!this.selected?.length) {
      return this.placeholder;
    }

    // When all options are selected or explicit Select All was clicked
    if (this.isFullySelected() || (this.showAllLabel && this.isFullySelected())) {
      return this.allLabel || 'All';
    }

    // Otherwise always show the concrete selection (same as Client).
    return this.selected
      .map((value) => this.getOptionLabel(value))
      .join(', ');
  }

  trackOption(_index: number, opt: PcMultiSelectOption): string {
    return opt.value;
  }

  getOptionLabel(value: string): string {
    return (
      this.resolvedOptions.find((opt) => opt.value === value)?.label ?? value
    );
  }

  isSelected(value: string): boolean {
    return (this.selected || []).includes(value);
  }

  isAllFilteredSelected(): boolean {
    if (!this.filteredOptions.length) {
      return false;
    }
    return this.filteredOptions.every((opt) => this.isSelected(opt.value));
  }

  private isFullySelected(): boolean {
    const opts = this.resolvedOptions;
    if (!opts.length || !this.selected?.length) {
      return false;
    }
    return opts.every((opt) => this.selected.includes(opt.value));
  }

  toggleDropdown(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    if (this.dropdownOpen) {
      this.closeDropdown();
      return;
    }

    if (
      PcMultiSelectFilterComponent.openInstance &&
      PcMultiSelectFilterComponent.openInstance !== this
    ) {
      PcMultiSelectFilterComponent.openInstance.closeDropdown();
    }

    this.dropdownOpen = true;
    PcMultiSelectFilterComponent.openInstance = this;
    this.searchText = '';
    this.cdr.detectChanges();
    this.schedulePanelPortal();
    this.scheduleOutsideClickHandler();
  }

  onOptionClick(event: MouseEvent, option: PcMultiSelectOption): void {
    event.preventDefault();
    event.stopPropagation();

    this.showAllLabel = false;
    const next = [...(this.selected || [])];
    const idx = next.indexOf(option.value);
    if (idx >= 0) {
      next.splice(idx, 1);
    } else {
      next.push(option.value);
    }
    this.emitSelection(next);
  }

  onSelectAllClick(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();

    const next = [...(this.selected || [])];
    if (this.isAllFilteredSelected()) {
      const filteredValues = new Set(
        this.filteredOptions.map((opt) => opt.value),
      );
      this.showAllLabel = false;
      this.emitSelection(next.filter((value) => !filteredValues.has(value)));
      return;
    }

    this.filteredOptions.forEach((opt) => {
      if (!next.includes(opt.value)) {
        next.push(opt.value);
      }
    });
    // Show "All" only when every option in the full list is selected.
    this.showAllLabel =
      this.resolvedOptions.length > 0 &&
      this.resolvedOptions.every((opt) => next.includes(opt.value));
    this.emitSelection(next);
  }

  clearAll(event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.showAllLabel = false;
    this.emitSelection([]);
  }

  /** Clears in-dropdown search text and closes the panel (e.g. after parent filter reset). */
  resetDropdownUi(): void {
    this.searchText = '';
    this.showAllLabel = false;
    this.closeDropdown(false);
  }

  private emitSelection(values: string[]): void {
    this.selected = [...values];
    if (!values.length) {
      this.showAllLabel = false;
    } else if (this.showAllLabel && !this.isFullySelected()) {
      this.showAllLabel = false;
    }
    this.selectedChange.emit([...values]);
    this.cdr.detectChanges();
  }

  private schedulePanelPortal(): void {
    setTimeout(() => {
      if (!this.dropdownOpen) {
        return;
      }
      this.attachPanelToBody();
    }, 0);
  }

  private attachPanelToBody(): void {
    const panel = this.panelRef?.nativeElement;
    if (!panel) {
      return;
    }

    this.syncPanelTheme(panel);

    if (this.panelPortaled) {
      this.updatePanelPosition();
      return;
    }

    this.renderer.appendChild(this.document.body, panel);
    panel.classList.add('pc-ms-filter__panel--overlay');
    this.syncPanelTheme(panel);
    this.panelPortaled = true;
    this.updatePanelPosition();
    window.addEventListener('scroll', this.onViewportChange, true);
    window.addEventListener('resize', this.onViewportChange);
  }

  private syncPanelTheme(panel: HTMLElement): void {
    panel.classList.toggle('pc-ms-filter__panel--light', this.lightMode);
  }

  private detachPanelFromHost(): void {
    const panel = this.panelRef?.nativeElement;
    if (!panel || !this.panelPortaled) {
      return;
    }

    panel.classList.remove('pc-ms-filter__panel--overlay');
    panel.classList.remove('pc-ms-filter__panel--light');
    panel.style.position = '';
    panel.style.top = '';
    panel.style.bottom = '';
    panel.style.left = '';
    panel.style.width = '';
    panel.style.maxHeight = '';
    panel.style.zIndex = '';

    this.renderer.appendChild(this.hostRef.nativeElement, panel);
    this.panelPortaled = false;
    window.removeEventListener('scroll', this.onViewportChange, true);
    window.removeEventListener('resize', this.onViewportChange);
  }

  private updatePanelPosition(): void {
    const panel = this.panelRef?.nativeElement;
    const trigger = this.triggerRef?.nativeElement;
    if (!panel || !trigger) {
      return;
    }

    const rect = trigger.getBoundingClientRect();
    const maxHeight = Math.min(320, window.innerHeight * 0.5);
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    const spaceAbove = rect.top - 8;

    panel.style.position = 'fixed';
    panel.style.left = `${rect.left}px`;
    panel.style.width = `${rect.width}px`;
    panel.style.zIndex = '20000';
    panel.style.right = 'auto';

    if (spaceBelow >= 160 || spaceBelow >= spaceAbove) {
      panel.style.top = `${rect.bottom + 4}px`;
      panel.style.bottom = 'auto';
      panel.style.maxHeight = `${Math.min(maxHeight, Math.max(spaceBelow, 120))}px`;
    } else {
      panel.style.top = 'auto';
      panel.style.bottom = `${window.innerHeight - rect.top + 4}px`;
      panel.style.maxHeight = `${Math.min(maxHeight, Math.max(spaceAbove, 120))}px`;
    }
  }

  private scheduleOutsideClickHandler(): void {
    this.clearOutsideClickHandler();
    this.outsideClickBindTimer = setTimeout(() => {
      this.outsideClickBindTimer = null;
      if (!this.dropdownOpen) {
        return;
      }

      this.outsideClickHandler = (event: MouseEvent) => {
        if (!this.dropdownOpen) {
          return;
        }

        const target = event.target as Node;
        const host = this.hostRef.nativeElement;
        const panel = this.panelRef?.nativeElement;
        if (host.contains(target) || panel?.contains(target)) {
          return;
        }

        this.closeDropdown();
      };

      this.document.addEventListener('click', this.outsideClickHandler, true);
    }, 0);
  }

  private clearOutsideClickHandler(): void {
    if (this.outsideClickBindTimer) {
      clearTimeout(this.outsideClickBindTimer);
      this.outsideClickBindTimer = null;
    }
    if (this.outsideClickHandler) {
      this.document.removeEventListener('click', this.outsideClickHandler, true);
      this.outsideClickHandler = null;
    }
  }

  private closeDropdown(emitClosed = true): void {
    const wasOpen = this.dropdownOpen;
    this.clearOutsideClickHandler();
    this.detachPanelFromHost();
    this.dropdownOpen = false;
    if (PcMultiSelectFilterComponent.openInstance === this) {
      PcMultiSelectFilterComponent.openInstance = null;
    }
    if (emitClosed && wasOpen) {
      this.dropdownClosed.emit();
    }
    this.cdr.detectChanges();
  }
}
